import { formatDistanceToNowStrict } from "date-fns";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { getCurrentEnv } from "~/common/utils";

// Cognito refresh-token validity for this user pool client, in days.
// Mirrors `userpoolClientRefreshTokenValidity` in
// portal/amplify/backend/auth/portalfc7df701/cli-inputs.json.
const REFRESH_TOKEN_VALIDITY_DAYS = 30;

const REFRESH_TOKEN_VALIDITY_MS =
  REFRESH_TOKEN_VALIDITY_DAYS * 24 * 60 * 60 * 1000;

// Show the warning when the refresh-token window has this much (or less) left.
export const SESSION_EXPIRY_WARNING_MS = 60 * 60 * 1000; // 1 hour

const TICK_MS = 60 * 1000; // recompute "time left" once a minute

// ── Dev-only tooltip simulator ──────────────────────────────────────────────
// window.__simulateProfileWarning(minutes) forces the hook to report `minutes`
// remaining (default 45) so the expiry tooltip can be verified without waiting
// for a real token to age; window.__clearProfileWarning() resets it. Wired up
// (dev env only) inside the hook. Backed by a tiny external store the hook
// subscribes to via useSyncExternalStore so the override drives a re-render.
let devOverrideMinutes: number | null = null;
const devOverrideListeners = new Set<() => void>();
const subscribeDevOverride = (onChange: () => void) => {
  devOverrideListeners.add(onChange);
  return () => devOverrideListeners.delete(onChange);
};
const setDevOverrideMinutes = (minutes: number | null) => {
  devOverrideMinutes = minutes;
  for (const listener of devOverrideListeners) listener();
};

export interface SessionExpiry {
  /** Absolute moment the refresh token stops working (user gets logged out). */
  expiresAt: Date | null;
  /** Milliseconds until `expiresAt`. Negative once expired. Null if unknown. */
  msUntilExpiry: number | null;
  /** Friendly time-left string, e.g. "45 minutes". Null when unknown/expired. */
  remainingLabel: string | null;
  /** True when within the warning threshold and not yet expired. */
  isWarning: boolean;
  /** True once the refresh-token window has elapsed. */
  isExpired: boolean;
}

/**
 * Tracks how long the current Cognito session has left before the refresh
 * token expires and the user is forced back to /login. Derived from the
 * `auth_time` claim on the ID token, which stays pinned to the original
 * sign-in time across access/ID token refreshes.
 */
export function useSessionExpiry(): SessionExpiry {
  const { authSession } = useAuthSession();
  const authTimeSec = authSession?.tokens?.idToken?.payload?.["auth_time"] as
    | number
    | undefined;

  // Keep the dependency a primitive so the tick interval isn't torn down and
  // recreated on every re-render (a fresh Date object would never be ===).
  const expiresAtMs =
    typeof authTimeSec === "number"
      ? authTimeSec * 1000 + REFRESH_TOKEN_VALIDITY_MS
      : null;

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (expiresAtMs === null) return;
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [expiresAtMs]);

  // Dev-only override (see setDevOverrideMinutes above).
  const overrideMinutes = useSyncExternalStore(
    subscribeDevOverride,
    () => devOverrideMinutes,
  );

  useEffect(() => {
    if (getCurrentEnv() !== "dev") return;
    const win = window as typeof window & {
      __simulateProfileWarning?: (minutes?: number) => void;
      __clearProfileWarning?: () => void;
    };
    win.__simulateProfileWarning = (minutes = 45) => {
      setDevOverrideMinutes(minutes);
      console.warn(`[dev] Simulating expiry tooltip: ${minutes} min left`);
    };
    win.__clearProfileWarning = () => setDevOverrideMinutes(null);
    return () => {
      win.__simulateProfileWarning = undefined;
      win.__clearProfileWarning = undefined;
    };
  }, []);

  if (overrideMinutes !== null) {
    const msUntilExpiry = overrideMinutes * 60 * 1000;
    const expiresAt = new Date(now + msUntilExpiry);
    return {
      expiresAt,
      msUntilExpiry,
      remainingLabel:
        msUntilExpiry > 0 ? formatDistanceToNowStrict(expiresAt) : null,
      isWarning:
        msUntilExpiry > 0 && msUntilExpiry <= SESSION_EXPIRY_WARNING_MS,
      isExpired: msUntilExpiry <= 0,
    };
  }

  if (expiresAtMs === null) {
    return {
      expiresAt: null,
      msUntilExpiry: null,
      remainingLabel: null,
      isWarning: false,
      isExpired: false,
    };
  }

  const expiresAt = new Date(expiresAtMs);
  const msUntilExpiry = expiresAtMs - now;
  return {
    expiresAt,
    msUntilExpiry,
    // e.g. "45 minutes" / "1 minute" / "30 seconds". Recomputed on each tick.
    remainingLabel:
      msUntilExpiry > 0 ? formatDistanceToNowStrict(expiresAt) : null,
    isWarning: msUntilExpiry > 0 && msUntilExpiry <= SESSION_EXPIRY_WARNING_MS,
    isExpired: msUntilExpiry <= 0,
  };
}
